import { useEffect, useState } from 'react'

import { saveDefaultProject } from '../../services/projectStorage.js'
import type { V2Runtime } from '../runtime/V2Runtime.js'
import { createProjectAutosaveController } from './ProjectAutosaveController.js'

/** Production document-only autosave. Playback and viewport never schedule IO. */
export function useProjectAutosave(runtime: V2Runtime | null, enabled: boolean): string | null {
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled || runtime === null) return
    let active = true
    const controller = createProjectAutosaveController(
      {
        getProject: () => runtime.store.getSnapshot().document,
        subscribe: (listener) => runtime.store.subscribe(listener),
      },
      saveDefaultProject,
      {
        onError: (reason) => {
          console.error('Failed to save the V2 project:', reason)
          if (active) setError('Project changes could not be saved. A later edit or reload will retry.')
        },
        onSaved: () => { if (active) setError(null) },
      },
    )

    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') void controller.flush()
    }
    const flushBeforePageHide = () => { void controller.flush() }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', flushWhenHidden)
      globalThis.addEventListener?.('pagehide', flushBeforePageHide)
    }

    return () => {
      active = false
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', flushWhenHidden)
        globalThis.removeEventListener?.('pagehide', flushBeforePageHide)
      }
      void controller.dispose()
    }
  }, [enabled, runtime])
  return error
}
